export type LongOperationPhase =
  'idle' | 'starting' | 'running' | 'cancelling' | 'complete' | 'failed';

export interface LongOperationStage<TStage extends string = string> {
  readonly id: TStage;
  readonly label: string;
}

export interface LongOperationState<TStage extends string = string> {
  readonly operationId: number;
  readonly phase: LongOperationPhase;
  readonly stage?: LongOperationStage<TStage>;
}

export interface LongOperationOptions<TStage extends string = string> {
  readonly signal?: AbortSignal;
  readonly onStage?: (stage: LongOperationStage<TStage>) => void;
  readonly onStageTiming?: (stage: TStage, durationMs: number) => void;
}

export class OperationCancelledError extends Error {
  constructor(message = 'Operation cancelled.') {
    super(message);
    this.name = 'OperationCancelledError';
  }
}

export function throwIfOperationCancelled(signal?: AbortSignal): void {
  if (signal?.aborted) throw new OperationCancelledError();
}

/** Give React and the browser a paint opportunity at a real, interruption-safe stage boundary. */
export function nextPaint(signal?: AbortSignal): Promise<void> {
  throwIfOperationCancelled(signal);
  return new Promise((resolve, reject) => {
    const frame = requestAnimationFrame(() => {
      cleanup();
      if (signal?.aborted) reject(new OperationCancelledError());
      else resolve();
    });
    const cancel = () => {
      cancelAnimationFrame(frame);
      cleanup();
      reject(new OperationCancelledError());
    };
    const cleanup = () => signal?.removeEventListener('abort', cancel);
    signal?.addEventListener('abort', cancel, { once: true });
  });
}
